/** One unanswered position on this phone. The other phone keeps its own. */
export interface PlayedPrompt {
  handshakeId: string;
  position: number;
  /** The coplayer, written the way the sheet writes a name. */
  coplayer: string;
  /** Meeting photos taken for this question. Yes moves these onto the position. */
  photos?: string[];
}

const PLAYED_PROMPT_KEY = 'gam_played_prompt';
/** Set when Play stores the question, so the clock does not ask again on that arrival. */
const PLAYED_PROMPT_HOLD_KEY = 'gam_played_prompt_hold';
/** Positions this phone was asked about. A No stays here so the positions list can still check it. */
const ASKED_POSITIONS_KEY = 'gam_asked_positions';

interface AskedPosition {
  handshakeId: string;
  position: number;
}

type KeyValueStore = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

const photoHashes = (value: unknown): string[] | null => {
  if (value == null) return [];
  if (!Array.isArray(value)) return null;

  const hashes = value.filter((hash) => typeof hash === 'string' && hash.length > 0);
  return hashes.length === value.length ? hashes : null;
};

const isPrompt = (value: unknown): value is PlayedPrompt => {
  if (!value || typeof value !== 'object') return false;

  const prompt = value as PlayedPrompt;

  return (
    typeof prompt.handshakeId === 'string' &&
    prompt.handshakeId.length > 0 &&
    Number.isInteger(prompt.position) &&
    prompt.position >= 0 &&
    prompt.position < 960 &&
    typeof prompt.coplayer === 'string' &&
    prompt.coplayer.length > 0 &&
    photoHashes(prompt.photos) != null
  );
};

/** The question Play left, if this phone has not answered it. */
export function readPlayedPrompt(storage: KeyValueStore = localStorage): PlayedPrompt | null {
  try {
    const raw = storage.getItem(PLAYED_PROMPT_KEY);
    if (!raw) return null;

    const parsed: unknown = JSON.parse(raw);
    if (!isPrompt(parsed)) return null;

    const photos = photoHashes(parsed.photos) ?? [];
    if (!photos.length) {
      delete parsed.photos;
      return parsed;
    }

    return { ...parsed, photos };
  } catch {
    return null;
  }
}

const isAsked = (value: unknown): value is AskedPosition => {
  if (!value || typeof value !== 'object') return false;

  const asked = value as AskedPosition;

  return (
    typeof asked.handshakeId === 'string' &&
    asked.handshakeId.length > 0 &&
    Number.isInteger(asked.position) &&
    asked.position >= 0 &&
    asked.position < 960
  );
};

const readAsked = (storage: KeyValueStore): AskedPosition[] => {
  try {
    const raw = storage.getItem(ASKED_POSITIONS_KEY);
    if (!raw) return [];

    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isAsked) : [];
  } catch {
    return [];
  }
};

/** Positions the dialogue asked about on this handshake. Saved or not, the list can show them. */
export function readAskedPositions(handshakeId: string, storage: KeyValueStore = localStorage): number[] {
  return readAsked(storage).flatMap((asked) => (asked.handshakeId === handshakeId ? [asked.position] : []));
}

/** Keep the question across a hard close. The hold marks the Play that just stored it. */
export function rememberPlayedPrompt(
  prompt: PlayedPrompt,
  storage: KeyValueStore = localStorage,
  session: KeyValueStore = sessionStorage,
): void {
  storage.setItem(PLAYED_PROMPT_KEY, JSON.stringify(prompt));
  session.setItem(PLAYED_PROMPT_HOLD_KEY, '1');

  const asked = readAsked(storage);
  if (!asked.some((item) => item.handshakeId === prompt.handshakeId && item.position === prompt.position)) {
    asked.push({ handshakeId: prompt.handshakeId, position: prompt.position });
    storage.setItem(ASKED_POSITIONS_KEY, JSON.stringify(asked));
  }
}

export function clearPlayedPrompt(storage: KeyValueStore = localStorage): void {
  storage.removeItem(PLAYED_PROMPT_KEY);
}

/** CLOCK is a return to the clock already open, so that arrival does not ask. */
export function holdPlayedPrompt(session: KeyValueStore = sessionStorage): void {
  session.setItem(PLAYED_PROMPT_HOLD_KEY, '1');
}

/** A photo just landed on the handshake for the question still waiting here. */
export function rememberPlayedPhotos(hashes: string[], storage: KeyValueStore = localStorage): void {
  const prompt = readPlayedPrompt(storage);
  const next = hashes.filter((hash) => hash.length > 0);
  if (!prompt || !next.length) return;

  const photos = [...new Set([...(prompt.photos ?? []), ...next])];
  storage.setItem(PLAYED_PROMPT_KEY, JSON.stringify({ ...prompt, photos }));
}

/**
 * True once, on the arrival Play just caused, or on a return through CLOCK.
 * A later opening of the clock has no hold, so the question comes up then.
 */
export function consumePlayedPromptHold(session: KeyValueStore = sessionStorage): boolean {
  if (!session.getItem(PLAYED_PROMPT_HOLD_KEY)) return false;

  session.removeItem(PLAYED_PROMPT_HOLD_KEY);

  return true;
}
